import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Tooltip,
  IconButton,
  Alert,
  AlertTitle,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import ConstructionIcon from '@mui/icons-material/Construction';
import Swal from 'sweetalert2';
import './IntegratedTools.css';

const IntegratedTools = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [showDevBanner, setShowDevBanner] = useState(true);
  const [tools, setTools] = useState({
    ofacApi: {
      name: 'OFAC Sanctions API',
      type: '',
      status: 'Inactive',
      calls: 120,
      lastUsed: '2025-04-15 09:00',
      apiKey: 'xxxx-xxxx-xxxx',
      endpoint: 'https://api.ofac.gov/sanctions',
      isLocked: true,
    },
    bloombergData: {
      name: 'Vessel Tracking',
      type: '',
      status: 'Active',
      calls: 50,
      lastUsed: '2025-04-14 15:30',
      apiKey: 'yyyy-yyyy-yyyy',
      endpoint: 'https://api.bloomberg.com/market',
      isLocked: false,
    },
    kycChecker: {
      name: 'Sanctions Screening',
      type: '',
      status: 'Active',
      calls: 80,
      lastUsed: '2025-04-15 10:00',
      apiKey: 'zzzz-zzzz-zzzz',
      endpoint: 'https://api.kyc.com/check',
      isLocked: false,
    },
  });

  const [transactionData] = useState([
    {
      field: 'BILL OF LADING NUMBER',
      transactionData: 'LMM0303494',
      returnedData: 'LMM0303494',
    },
    {
      field: 'CONTAINER NUMBER(S)',
      transactionData: 'TCNU3603359',
      returnedData: 'CMAU4366498, CMAU438182...',
    },
    {
      field: 'PORT OF DISCHARGE',
      transactionData: 'HUANGPU, CHINA',
      returnedData: 'HUANGPU, CHINA',
    },
    {
      field: 'PORT OF LOADING',
      transactionData: 'TAHOMA, US',
      returnedData: 'TAHOMA, US',
    },
    {
      field: 'ON BOARD DATE',
      transactionData: '20 JUNE 2022',
      returnedData: 'JUN 20, 2022',
    },
  ]);

  const [logs, setLogs] = useState([
    { id: 'log1', tool: 'OFAC Sanctions API', action: 'Sanctions check called', timestamp: '2025-04-15 09:00' },
    { id: 'log2', tool: 'Bloomberg Market Data', action: 'Data fetch attempted', timestamp: '2025-04-14 15:30' },
  ]);
  const [editTool, setEditTool] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [newTool, setNewTool] = useState({ name: '', type: '', apiKey: '', endpoint: '' });
  const [openAddModal, setOpenAddModal] = useState(false);

  const handleTestTool = (toolId) => {
    Swal.fire({
      icon: 'info',
      title: 'Test Result',
      text: `Mock test for ${tools[toolId].name} successful.`,
      timer: 1500,
      customClass: {
        container: 'toast-success',
      },
    });
  };

  const handleReconnect = (toolId) => {
    setTools((prev) => ({
      ...prev,
      [toolId]: { ...prev[toolId], status: 'Active' },
    }));
    Swal.fire({
      icon: 'success',
      title: 'Reconnected',
      text: `${tools[toolId].name} is now active (mock).`,
      timer: 1500,
      customClass: {
        container: 'toast-success',
      },
    });
  };

  const handleEditTool = (toolId) => {
    setEditTool({ id: toolId, ...tools[toolId] });
    setOpenModal(true);
  };

  const handleSaveEdit = () => {
    if (!editTool.name || !editTool.apiKey || !editTool.endpoint) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Input',
        text: 'All fields are required.',
        customClass: {
          container: 'toast-error',
        },
      });
      return;
    }
    setTools((prev) => ({
      ...prev,
      [editTool.id]: {
        ...prev[editTool.id],
        name: editTool.name,
        apiKey: editTool.apiKey,
        endpoint: editTool.endpoint,
      },
    }));
    setOpenModal(false);
    setEditTool(null);
    Swal.fire({
      icon: 'success',
      title: 'Settings Updated',
      text: 'Tool configuration saved (mock).',
      timer: 1500,
      customClass: {
        container: 'toast-success',
      },
    });
  };

  const handleAddTool = () => {
    if (!newTool.name || !newTool.type || !newTool.apiKey || !newTool.endpoint) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Input',
        text: 'All fields are required.',
        customClass: {
          container: 'toast-error',
        },
      });
      return;
    }
    const toolId = `tool${Object.keys(tools).length + 1}`;
    setTools((prev) => ({
      ...prev,
      [toolId]: {
        name: newTool.name,
        type: newTool.type,
        status: 'Active',
        calls: 0,
        lastUsed: 'Not Used',
        apiKey: newTool.apiKey,
        endpoint: newTool.endpoint,
        isLocked: false,
      },
    }));
    setOpenAddModal(false);
    setNewTool({ name: '', type: '', apiKey: '', endpoint: '' });
    Swal.fire({
      icon: 'success',
      title: 'Tool Added',
      text: 'New integration added (mock).',
      timer: 1500,
      customClass: {
        container: 'toast-success',
      },
    });
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(tools, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'tools_config.json';
    link.click();
    URL.revokeObjectURL(url);
    Swal.fire({
      icon: 'success',
      title: 'Exported',
      text: 'Tool settings exported as JSON.',
      timer: 1500,
      customClass: {
        container: 'toast-success',
      },
    });
  };

  const handleDownloadReport = () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL}/Result Report (1).pdf`;
    link.download = 'Result Report (1).pdf';
    link.click();
    Swal.fire({
      icon: 'success',
      title: 'Downloaded',
      text: 'Screening report downloaded successfully.',
      timer: 1500,
      customClass: {
        container: 'toast-success',
      },
    });
  };

  const filteredTools = Object.entries(tools).filter(([_, tool]) => {
    const matchesSearch = tool.name.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filterType === 'All' || tool.type === filterType;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="integrated-tools-container">
      <Box className="integrated-tools-content">
        {/* Under Development Banner */}
        <Collapse in={showDevBanner}>
          <Alert
            severity="warning"
            icon={<ConstructionIcon fontSize="inherit" />}
            className="dev-banner"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setShowDevBanner(false)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{
              mb: 2,
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              borderLeft: '4px solid #ff9800',
              '& .MuiAlert-icon': {
                color: '#ff9800',
              },
              '& .MuiAlert-message': {
                color: 'var(--text-color)',
              },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600 }}>Under Development</AlertTitle>
            This page is currently under development. Some features may not work as expected.
          </Alert>
        </Collapse>

        <Typography variant="h4" className="section-title">
          Integrated Tools
        </Typography>
        <Box className="integrated-tools-toolbar">
          <TextField
            placeholder="Search Tools"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'var(--text-color)' }} />
                </InputAdornment>
              ),
            }}
            className="toolbar-search"
            sx={{
              '& .MuiInputBase-input': { color: 'var(--text-color)' },
              '& .MuiInputLabel-root': { color: 'var(--text-muted)' },
              '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
            }}
          />
          <FormControl className="toolbar-filter">
            <InputLabel sx={{ color: 'var(--text-muted)', '&.Mui-focused': { color: 'var(--primary-color)' } }}>
              Type
            </InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              sx={{
                color: 'var(--text-color)',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-color)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-hover-color)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--primary-color)' },
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Compliance">Compliance</MenuItem>
              <MenuItem value="Market Data">Market Data</MenuItem>
              <MenuItem value="Screening">Screening</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Add New Integration">
            <Button
              className="btn-contained toolbar-add"
              onClick={() => setOpenAddModal(true)}
            >
              <AddIcon sx={{ color: 'var(--text-color)' }} />
            </Button>
          </Tooltip>
        </Box>
        <Box className="integrated-tools-section">
          <Typography variant="h6" className="section-title">
            Tools
          </Typography>
          <Box className="tools-list">
            {filteredTools.map(([toolId, tool]) => (
              <Box key={toolId} sx={{ position: 'relative' }}>
                <Accordion 
                  className="card tool-item"
                  disabled={tool.isLocked}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--text-color)' }} />}>
                    <Box className="tool-summary">
                      <Typography className="tool-name">{tool.name}</Typography>
                      {tool.type && (
                        <Chip
                          label={tool.type}
                          className={`type-badge type-${tool.type.toLowerCase().replace(' ', '-')}`}
                        />
                      )}
                      <Chip
                        label={tool.status}
                        className="status-chip"
                        style={{
                          background: tool.status === 'Active' ? 'var(--success-color)' : 'var(--text-muted)',
                          color: 'var(--text-color)',
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box className="tool-details">
                      {toolId === 'kycChecker' ? (
                        <>
                          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                            List Screening Results (477 matches found)
                          </Typography>
                          <Table size="small" className="screening-table" sx={{ mb: 2 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>No.</TableCell>
                                <TableCell>Score</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Date of Birth</TableCell>
                                <TableCell>Gender</TableCell>
                                <TableCell>ID Numbers</TableCell>
                                <TableCell>Address</TableCell>
                                <TableCell>Country</TableCell>
                                <TableCell>Entity Type</TableCell>
                                <TableCell>Citizenship</TableCell>
                                <TableCell>Report</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell>1</TableCell>
                                <TableCell>100</TableCell>
                                <TableCell>Noor Mohammad<br/>✓ Noor Muhammad (AKA)</TableCell>
                                <TableCell>1945</TableCell>
                                <TableCell>Male</TableCell>
                                <TableCell>3105089 (Proprietary UID)</TableCell>
                                <TableCell>Pakistan<br/>Zarghoon Road Quetta Balochistan Pakistan</TableCell>
                                <TableCell>Pakistan</TableCell>
                                <TableCell>✓ Individual</TableCell>
                                <TableCell>Pakistan</TableCell>
                                <TableCell>
                                  <Tooltip title="Download Report">
                                    <IconButton 
                                      size="small" 
                                      onClick={handleDownloadReport}
                                      sx={{ color: 'var(--primary-color)' }}
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>2</TableCell>
                                <TableCell>100</TableCell>
                                <TableCell>Noor Mohammed<br/>✓ Noor</TableCell>
                                <TableCell>-</TableCell>
                                <TableCell>Male</TableCell>
                                <TableCell>8836013 (Proprietary UID)</TableCell>
                                <TableCell>Pakistan</TableCell>
                                <TableCell>Pakistan</TableCell>
                                <TableCell>✓ Individual</TableCell>
                                <TableCell>Pakistan</TableCell>
                                <TableCell>
                                  <Tooltip title="Download Report">
                                    <IconButton 
                                      size="small" 
                                      onClick={handleDownloadReport}
                                      sx={{ color: 'var(--primary-color)' }}
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </>
                      ) : toolId === 'bloombergData' ? (
                        <>
                          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'var(--text-color)' }}>
                            Transaction Data Comparison
                          </Typography>
                          <Table 
                            size="small" 
                            sx={{ 
                              mb: 2,
                              '& .MuiTableCell-head': {
                                backgroundColor: '#1e3a5f',
                                color: '#fff',
                                fontWeight: 600,
                                padding: '12px 16px',
                                borderBottom: '2px solid #2c5282',
                              },
                              '& .MuiTableCell-body': {
                                padding: '12px 16px',
                                borderBottom: '1px solid #e2e8f0',
                              },
                              '& .MuiTableRow-root:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              },
                            }}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: '33%' }}>Value</TableCell>
                                <TableCell sx={{ width: '33%' }}>Transaction Data</TableCell>
                                <TableCell sx={{ width: '33%' }}>Vessel Tracking Data</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {transactionData.map((row, index) => (
                                <TableRow key={index}>
                                  <TableCell sx={{ fontWeight: 500 }}>{row.field}</TableCell>
                                  <TableCell>{row.transactionData}</TableCell>
                                  <TableCell>{row.returnedData}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </>
                      ) : (
                        <>
                          <LinearProgress
                            variant="determinate"
                            value={tool.status === 'Active' ? Math.random() * 100 : 0}
                            className="tool-progress"
                            sx={{
                              backgroundColor: 'var(--border-color)',
                              '& .MuiLinearProgress-bar': {
                                background: 'var(--gradient-primary)',
                              },
                            }}
                          />
                        </>
                      )}
                      {tool.type && <Typography>Type: {tool.type}</Typography>}
                      <Typography>API Calls: {tool.calls}</Typography>
                      <Typography>Last Used: {tool.lastUsed}</Typography>
                      <Box className="tool-actions">
                        <Tooltip title="Test this integration">
                          <Button
                            className="btn-outlined"
                            onClick={() => handleTestTool(toolId)}
                          >
                            Test Tool
                          </Button>
                        </Tooltip>
                        {tool.status === 'Inactive' && (
                          <Tooltip title="Reconnect this tool">
                            <Button
                              className="btn-outlined"
                              onClick={() => handleReconnect(toolId)}
                            >
                              Reconnect
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip title="Configure settings">
                          <IconButton className="icon-wrapper" onClick={() => handleEditTool(toolId)}>
                            <SettingsIcon sx={{ color: 'var(--text-color)' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
                
                {tool.isLocked && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      cursor: 'not-allowed',
                      zIndex: 10,
                    }}
                  >
                    <LockIcon 
                      sx={{ 
                        fontSize: '80px',
                        color: '#888',
                        filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
                      }} 
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
        <Box className="integrated-tools-section">
          <Typography variant="h6" className="section-title">
            Activity Logs
          </Typography>
          <Table className="table integrated-tools-table">
            <TableHead>
              <TableRow>
                <TableCell>Tool</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{log.tool}</TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell>{log.timestamp}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Box className="integrated-tools-actions">
      
        </Box>
        <Dialog open={openModal} onClose={() => setOpenModal(false)} className="modal tool-dialog">
          <DialogTitle>Configure Tool</DialogTitle>
          <DialogContent>
            <TextField
              label="Tool Name"
              value={editTool?.name || ''}
              onChange={(e) => setEditTool({ ...editTool, name: e.target.value })}
              fullWidth
              sx={{
                mt: 2,
                '& .MuiInputBase-input': { color: 'var(--text-color)' },
                '& .MuiInputLabel-root': { color: 'var(--text-muted)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
              }}
            />
            <TextField
              label="API Key"
              value={editTool?.apiKey || ''}
              onChange={(e) => setEditTool({ ...editTool, apiKey: e.target.value })}
              fullWidth
              sx={{
                mt: 2,
                '& .MuiInputBase-input': { color: 'var(--text-color)' },
                '& .MuiInputLabel-root': { color: 'var(--text-muted)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
              }}
            />
            <TextField
              label="Endpoint"
              value={editTool?.endpoint || ''}
              onChange={(e) => setEditTool({ ...editTool, endpoint: e.target.value })}
              fullWidth
              sx={{
                mt: 2,
                '& .MuiInputBase-input': { color: 'var(--text-color)' },
                '& .MuiInputLabel-root': { color: 'var(--text-muted)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button className="btn-outlined" onClick={() => setOpenModal(false)}>
              Cancel
            </Button>
            <Button className="btn-contained" onClick={handleSaveEdit}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} className="modal tool-dialog">
          <DialogTitle>Add New Integration</DialogTitle>
          <DialogContent>
            <TextField
              label="Tool Name"
              value={newTool.name}
              onChange={(e) => setNewTool({ ...newTool, name: e.target.value })}
              fullWidth
              sx={{
                mt: 2,
                '& .MuiInputBase-input': { color: 'var(--text-color)' },
                '& .MuiInputLabel-root': { color: 'var(--text-muted)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
              }}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel sx={{ color: 'var(--text-muted)', '&.Mui-focused': { color: 'var(--primary-color)' } }}>
                Type
              </InputLabel>
              <Select
                value={newTool.type}
                onChange={(e) => setNewTool({ ...newTool, type: e.target.value })}
                sx={{
                  color: 'var(--text-color)',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-color)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-hover-color)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--primary-color)' },
                }}
              >
                <MenuItem value="Compliance">Compliance</MenuItem>
                <MenuItem value="Market Data">Market Data</MenuItem>
                <MenuItem value="Screening">Screening</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="API Key"
              value={newTool.apiKey}
              onChange={(e) => setNewTool({ ...newTool, apiKey: e.target.value })}
              fullWidth
              sx={{
                mt: 2,
                '& .MuiInputBase-input': { color: 'var(--text-color)' },
                '& .MuiInputLabel-root': { color: 'var(--text-muted)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
              }}
            />
            <TextField
              label="Endpoint"
              value={newTool.endpoint}
              onChange={(e) => setNewTool({ ...newTool, endpoint: e.target.value })}
              fullWidth
              sx={{
                mt: 2,
                '& .MuiInputBase-input': { color: 'var(--text-color)' },
                '& .MuiInputLabel-root': { color: 'var(--text-muted)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button className="btn-outlined" onClick={() => setOpenAddModal(false)}>
              Cancel
            </Button>
            <Button className="btn-contained" onClick={handleAddTool}>
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
};

export default IntegratedTools;